'use client';
import { Box, Button, Link } from '@mui/material';
import { useMemo } from 'react';

import {
  AbgTypography,
  BookmarkIcon,
  FormError,
  FormErrorConfig,
  mergeSx,
  useTranslation,
} from '@abg/kit-base';

import { LoginFormData } from '../../../shared/types/LoginFrom.interface';
import { userStoreSelector } from '../../../store';
import { SocialLoginType } from '../constants';
import { useLoginJourneyContext } from '../LoginJourneyContext';

import { AmazonLogin } from './molecules/AmazonLogin';
import { InnerForm } from './molecules/InnerForm';
import { InnerFormPasswordLess } from './molecules/InnerFormPasswordLess';
import { loginFormBodyStyling } from './LoginFormBody.styling';
import { LoginFormBodyProps } from './types';

export const LoginFormBody = ({
  styleParts = {},
  partProps = {},
  model,
  isOtpFlow,
}: LoginFormBodyProps) => {
  const t = useTranslation(model.i18n);
  const userSession = userStoreSelector.use.userSession();

  const { loginJourney, amazonLoginJourney } = useLoginJourneyContext();
  const { errorData: loginErrorData, handleSubmitLogin } = loginJourney;
  const { errorData: amazonErrorData } = amazonLoginJourney;

  const { amazonLogin } = userSession || {};

  const handleSubmit = (data: LoginFormData) => {
    return handleSubmitLogin(data);
  };

  const error = useMemo<FormErrorConfig | null>(() => {
    return loginErrorData || amazonErrorData || null;
  }, [amazonErrorData, loginErrorData]);

  return (
    <>
      {model.memberBenefitsBannerText && (
        <Box
          sx={mergeSx(
            loginFormBodyStyling.content.banner.wrapper,
            styleParts.banner,
          )}
        >
          <BookmarkIcon sx={loginFormBodyStyling.content.banner.icon} />

          <AbgTypography
            data-testid="login-dialog-banner-text"
            sx={loginFormBodyStyling.content.banner.text}
          >
            {model.memberBenefitsBannerText}
          </AbgTypography>
        </Box>
      )}

      {!!error && (
        <Box data-testid="login-form-error">
          <FormError
            styleParts={partProps.formError?.styleParts}
            config={error}
            testIdPrefix="login-dialog"
          />
        </Box>
      )}

      {isOtpFlow && (
        <InnerFormPasswordLess
          styleParts={styleParts}
          partProps={partProps}
          model={model}
          onSubmit={handleSubmit}
        />
      )}

      {!isOtpFlow && (
        <InnerForm
          styleParts={styleParts}
          partProps={partProps}
          model={model}
          onSubmit={handleSubmit}
        />
      )}

      {!amazonLogin && model.socialLinks?.includes(SocialLoginType.Amazon) && (
        <AmazonLogin
          styleParts={styleParts}
          deviderText={t('loginModal.orLoginWith.label')}
          btnCaption={t('loginModal.amazonCta.label')}
        />
      )}

      {!model.showSimplifiedDriveForm && (
        <Button
          component={Link}
          href={model.createAccountLink}
          target={model.createAccountTarget}
          data-testid="login-dialog-signup-link"
          sx={mergeSx(
            loginFormBodyStyling.content.signUpBtn,
            styleParts.signUpBtn,
          )}
          className="tertiary"
        >
          {model.createAccountText}
        </Button>
      )}
    </>
  );
};
